import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatCents, type RecipeSettingsResponse } from '@familyhub/shared';
import { getRecipeSettings, putRecipeSettings } from './settings';
import { useRefreshOnReturn } from '../useRefreshOnReturn';

/**
 * The recepten config screen (`/recepten/instellingen`): pick which AI model
 * writes this household's recipes, with a rough per-recipe price for each.
 *
 * Online-only, like the rest of the recepten AI (import/generate/adjust). The
 * catalog and the current choice come from `GET /recepten/settings`; a choice is
 * saved with `PUT`. A module-level cache keeps the last answer across navigation
 * (the WeatherCard idiom), and the choice is shared across the household's phones
 * because it lives on the server.
 */

let cached: RecipeSettingsResponse | null = null;

function priceLabel(cents: number): string {
  // 0 means "under a cent" — the estimate rounds sub-cent models to nothing.
  return cents <= 0 ? '< €0,01 per recept' : `± ${formatCents(cents) ?? ''} per recept`;
}

export function RecipeSettingsScreen() {
  const [data, setData] = useState<RecipeSettingsResponse | null>(cached);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await getRecipeSettings();
      cached = res;
      setData(res);
      setFailed(false);
    } catch {
      // Keep the last answer if we have one; only admit a problem with nothing to show.
      if (!cached) setFailed(true);
    }
  }, []);

  useRefreshOnReturn(refresh);
  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function choose(id: string) {
    if (busy || data?.recipe_model === id) return;
    setBusy(id);
    try {
      const res = await putRecipeSettings({ recipe_model: id });
      cached = res;
      setData(res);
    } catch {
      // A failed save just leaves the choice where it was.
    } finally {
      setBusy(null);
    }
  }

  // The effective model is normally one of the catalog options; a deploy default
  // outside the list (e.g. a local Ollama model) is shown as "Huidig" instead.
  const known = data ? data.models.some((m) => m.id === data.recipe_model) : true;

  return (
    <div className="app">
      <div className="topbar">
        <Link className="btn btn-quiet back" to="/recepten" aria-label="Naar recepten">
          ‹ Recepten
        </Link>
        <h1>Recept-AI</h1>
        <span style={{ width: 52 }} aria-hidden />
      </div>

      <main className="stack">
        <p className="row-sub wraps">
          Kies welk AI-model recepten schrijft. Duurdere modellen schrijven doorgaans betere
          recepten. De prijs is een schatting per gegenereerd recept.
        </p>

        {data === null ? (
          <p className="row-sub">{failed ? 'Instellingen niet beschikbaar.' : 'Laden…'}</p>
        ) : (
          <>
            <p className="section-label">Model</p>
            <div className="card" role="radiogroup" aria-label="AI-model voor recepten">
              {data.models.map((m) => {
                const selected = m.id === data.recipe_model;
                return (
                  <div className="row row-top" key={m.id}>
                    <span className="row-main">
                      <span className="row-name">{m.label}</span>
                      <span className="row-sub">{priceLabel(m.est_cents_per_recipe)}</span>
                    </span>
                    <button
                      className="btn btn-quiet"
                      role="radio"
                      aria-checked={selected}
                      aria-label={selected ? `${m.label} gekozen` : `Kies ${m.label}`}
                      disabled={busy !== null || selected}
                      onClick={() => void choose(m.id)}
                    >
                      {selected ? 'Gekozen' : busy === m.id ? '…' : 'Kies'}
                    </button>
                  </div>
                );
              })}
            </div>

            {!known && <p className="row-sub wraps">Huidig model: {data.recipe_model}</p>}
          </>
        )}
      </main>
    </div>
  );
}
